import { useEffect, useState } from 'react';
import { DinhMucNguyenVatLieu, DinhMucNguyenVatLieuInput } from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

interface UseDinhMucNguyenVatLieuReturn {
  dinhMucNguyenVatLieus: DinhMucNguyenVatLieu[];
  isLoading: boolean;
  addDinhMucNguyenVatLieu: (newDinhMucNguyenVatLieu: DinhMucNguyenVatLieuInput) => Promise<void>;
  updateDinhMucNguyenVatLieu: (uuid: string, updatedDinhMucNguyenVatLieu: DinhMucNguyenVatLieuInput) => Promise<void>;
  deleteDinhMucNguyenVatLieu: (uuid: string) => Promise<void>;
  refreshDinhMucNguyenVatLieus: () => Promise<void>;
}
interface DinhMucNguyenVatLieuResponse {
  results: DinhMucNguyenVatLieu[];
  count: number;
}

export const useDinhMucNguyenVatLieu = (
  initialDinhMucNguyenVatLieus: DinhMucNguyenVatLieu[] = []
): UseDinhMucNguyenVatLieuReturn => {
  const [dinhMucNguyenVatLieus, setDinhMucNguyenVatLieus] =
    useState<DinhMucNguyenVatLieu[]>(initialDinhMucNguyenVatLieus);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchDinhMucNguyenVatLieus = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.DINH_MUC_NGUYEN_VAT_LIEU}/`);
      const mappedData: DinhMucNguyenVatLieu[] = response.data.results;
      setDinhMucNguyenVatLieus(mappedData);
    } catch (error) {
      console.error('Error fetching material standards:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addDinhMucNguyenVatLieu = async (newDinhMucNguyenVatLieu: DinhMucNguyenVatLieuInput) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.post(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DINH_MUC_NGUYEN_VAT_LIEU}/`,
        newDinhMucNguyenVatLieu
      );
      const addedDinhMucNguyenVatLieu: DinhMucNguyenVatLieu = response.data;
      setDinhMucNguyenVatLieus(prev => [...prev, addedDinhMucNguyenVatLieu]);
    } catch (error) {
      console.error('Error adding material standard:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateDinhMucNguyenVatLieu = async (uuid: string, updatedDinhMucNguyenVatLieu: DinhMucNguyenVatLieuInput) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.put(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DINH_MUC_NGUYEN_VAT_LIEU}/${uuid}/`,
        updatedDinhMucNguyenVatLieu
      );
      const updatedData: DinhMucNguyenVatLieu = response.data;
      setDinhMucNguyenVatLieus(prev => prev.map(item => (item.uuid === updatedData.uuid ? updatedData : item)));
    } catch (error) {
      console.error('Error updating material standard:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDinhMucNguyenVatLieu = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.DINH_MUC_NGUYEN_VAT_LIEU}/${uuid}/`);
      setDinhMucNguyenVatLieus(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting material standard:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDinhMucNguyenVatLieus();
  }, [entity?.slug]);

  return {
    dinhMucNguyenVatLieus,
    isLoading,
    addDinhMucNguyenVatLieu,
    updateDinhMucNguyenVatLieu,
    deleteDinhMucNguyenVatLieu,
    refreshDinhMucNguyenVatLieus: fetchDinhMucNguyenVatLieus
  };
};

export const fetchAllDinhMucNguyenVatLieu = async (
  entitySlug: string,
  pageSize = 200
): Promise<DinhMucNguyenVatLieu[]> => {
  let page = 1;
  let all: DinhMucNguyenVatLieu[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<DinhMucNguyenVatLieuResponse>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.DINH_MUC_NGUYEN_VAT_LIEU}/`,
      {
        params: {
          page,
          page_size: pageSize
        }
      }
    );

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
